import classNames from "classnames";
import { useMemo } from "react";
import { Icon } from "@livingmap/hippo";
import { useTranslation } from "react-i18next";

import { FloorChange, FloorDifference } from "utils/getFloorDifference";

import styles from "./DirectionChip.module.scss";

export type DirectionChipAppearance = "plain" | "tinted" | "solid";

export interface DirectionChipProps {
  className?: string;
  dataQA: string;
  floorChange?: FloorDifference | null;
  distance?: string | null;
  appearance?: DirectionChipAppearance;
  /** Use the text-over-image token when the chip sits atop imagery. */
  textOverImage?: boolean;
}

export default function DirectionChip({
  appearance,
  className,
  dataQA,
  floorChange,
  distance,
  textOverImage = false,
}: DirectionChipProps) {
  const { t } = useTranslation();

  const resolvedAppearance: DirectionChipAppearance = appearance ?? "plain";

  const hasFloorChange = Boolean(floorChange);

  const floorDifference: FloorDifference = floorChange ?? {
    amount: 0,
    direction: FloorChange.NONE,
  };

  const levelLabel = useMemo(() => {
    if (floorDifference.amount === 0) return t("floor_change.no_change");

    const difference = t("floor_change.difference", {
      count: floorDifference.amount,
    });
    const direction = t(`floor_change.${floorDifference.direction}`);

    return `${floorDifference.amount} ${difference} ${direction}`;
  }, [floorDifference.amount, floorDifference.direction, t]);

  const showLevelIcon =
    hasFloorChange &&
    floorDifference.amount > 0 &&
    floorDifference.direction !== FloorChange.NONE;
  const levelIconName =
    floorDifference.direction === FloorChange.UP
      ? "stairs_up"
      : floorDifference.direction === FloorChange.DOWN
        ? "stairs_down"
        : "stairs";

  const showDistance = distance !== undefined && distance !== null;

  return (
    <div
      data-qa={dataQA}
      className={classNames(
        styles.container,
        styles[resolvedAppearance],
        { [styles.textOverImage]: textOverImage },
        className,
      )}
    >
      {hasFloorChange && (
        <div className={styles.segment}>
          {showLevelIcon && (
            <Icon
              dataQA="direction-chip-level-icon"
              name={levelIconName}
              size={24}
              className={classNames(styles.icon, {
                [styles.down]: floorDifference.direction === FloorChange.DOWN,
              })}
            />
          )}
          <span className={styles.text}>{levelLabel}</span>
        </div>
      )}
      {showDistance && (
        <div className={classNames(styles.segment, styles.distance)}>
          <Icon
            dataQA="direction-chip-distance-icon"
            name="directions_walk"
            size={24}
            className={styles.icon}
          />
          <span className={styles.text}>
            <span className={styles.value}>{distance}</span>
          </span>
        </div>
      )}
    </div>
  );
}
